import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.plus_pass_ad_failed
import banko.composeapp.generated.resources.plus_pass_loading
import banko.composeapp.generated.resources.plus_pass_watch
import kotlinx.coroutines.delay

/**
 * The shared watch-a-rewarded-ad button used by every ad-reward offer (Plus Pass, dice trials,
 * end-game renewal). Arms only once an ad is actually loaded — so a tap can never race the network
 * — showing a loading label until then, and retries a failed load a few times.
 * [onRewarded] fires only after the ad pays out; an early close or show failure surfaces inline.
 */
@Composable
internal fun WatchAdButton(
    onRewarded: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val adReady by BankoRewardedAd.isReady.collectAsState()
    var showingAd by remember { mutableStateOf(false) }
    var adFailed by remember { mutableStateOf(false) }
    // A failed load leaves adReady false, which never re-keys this effect, so retry a few times
    // while the button is on screen. Capped: the effect keeps running while the app is stopped.
    LaunchedEffect(adReady) {
        if (adReady) return@LaunchedEffect
        repeat(REWARDED_AD_MAX_LOADS) {
            BankoRewardedAd.preload()
            delay(REWARDED_AD_RETRY_MS)
        }
    }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Button(
            onClick = {
                adFailed = false
                showingAd = true
                BankoRewardedAd.show { rewarded ->
                    showingAd = false
                    if (rewarded) onRewarded() else adFailed = true
                }
            },
            enabled = adReady && !showingAd,
            shape = RoundedCornerShape(16.dp),
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(
                text = stringResource(
                    if (adReady || showingAd) Res.string.plus_pass_watch
                    else Res.string.plus_pass_loading
                )
            )
        }
        if (adFailed) {
            Text(
                text = stringResource(Res.string.plus_pass_ad_failed),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.error,
            )
        }
    }
}

private const val REWARDED_AD_RETRY_MS = 30_000L
private const val REWARDED_AD_MAX_LOADS = 5
